import React, { useEffect, useState } from "react";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  setDoc,
} from "firebase/firestore";
import { toast } from "react-toastify";
import { db } from "../firebase";
import { useUser } from "../context/UserContext";
import { ADMIN_CONTENT_DOC_ID } from "../constants/adminContent";
import { getClientEditMeta, writeFullAuditLog } from "../utils/editAudit";

function isAuthMarkerDoc(id, data) {
  if (id === ADMIN_CONTENT_DOC_ID) return false;
  // UID-style ids are the login gate; keep listing them even if legacy content remains.
  return typeof id === "string" && id.length >= 20;
}

const AdminsPage = () => {
  const { user } = useUser();
  const [admins, setAdmins] = useState([]);
  const [uid, setUid] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(true);

  const loadAdmins = async () => {
    setListLoading(true);
    try {
      const snap = await getDocs(collection(db, "admin"));
      const rows = snap.docs
        .filter((d) => isAuthMarkerDoc(d.id, d.data()))
        .map((d) => ({
          id: d.id,
          ...d.data(),
        }));
      setAdmins(rows);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load admins");
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    loadAdmins();
  }, []);

  const handleGrant = async (event) => {
    event.preventDefault();
    const trimmedUid = uid.trim();
    if (!trimmedUid) {
      toast.error("UID is required");
      return;
    }
    if (trimmedUid === ADMIN_CONTENT_DOC_ID) {
      toast.error("That ID is reserved for app content");
      return;
    }

    setLoading(true);
    try {
      const audit = await getClientEditMeta({
        section: "admins",
        action: "grant",
        targetId: trimmedUid,
      });
      const stamp = { ...audit };
      delete stamp._geo;

      await setDoc(
        doc(db, "admin", trimmedUid),
        {
          role: "admin",
          admin_email: email.trim() || null,
          granted_at: stamp.last_edit_at || new Date().toISOString(),
          granted_by: user?.email || user?.uid || null,
          ...stamp,
        },
        { merge: true }
      );

      await writeFullAuditLog({
        section: "admins",
        action: "grant",
        collectionName: "admin",
        targetId: trimmedUid,
        before: null,
        after: { role: "admin", admin_email: email.trim() || null },
        meta: audit,
      });

      toast.success("Admin access granted");
      setUid("");
      setEmail("");
      loadAdmins();
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Failed to grant admin");
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (adminId) => {
    if (adminId === user?.uid) {
      toast.error("You cannot revoke your own admin access");
      return;
    }
    if (!window.confirm(`Revoke admin access for ${adminId}?`)) return;

    setLoading(true);
    try {
      const audit = await getClientEditMeta({
        section: "admins",
        action: "revoke",
        targetId: adminId,
      });

      await deleteDoc(doc(db, "admin", adminId));

      await writeFullAuditLog({
        section: "admins",
        action: "revoke",
        collectionName: "admin",
        targetId: adminId,
        before: { id: adminId },
        after: null,
        meta: audit,
      });

      toast.success("Admin access revoked");
      loadAdmins();
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Failed to revoke admin");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4 mb-5">
      <h2 className="mb-2">Admins</h2>
      <p className="text-muted mb-3">
        Dashboard access requires an <code>admin/&#123;UID&#125;</code> document.
        Website and app content live in <code>admin/app_content</code> and stay
        in place when admin accounts change.
      </p>
      <div className="alert alert-info small">
        To transfer admin access: create the user in Firebase Authentication →
        copy the UID → add it here. Content (About / FAQ / Social…) stays the
        same.
      </div>

      <form onSubmit={handleGrant} className="card p-3 shadow-sm mb-4">
        <h5 className="mb-3">Grant admin access</h5>
        <div className="mb-3">
          <label className="form-label">Firebase Auth UID</label>
          <input
            className="form-control"
            value={uid}
            onChange={(e) => setUid(e.target.value)}
            placeholder="e.g. xY7...abc"
            required
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Email (optional note)</label>
          <input
            type="email"
            className="form-control"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@example.com"
          />
        </div>
        <button className="btn btn-primary" type="submit" disabled={loading}>
          {loading ? "Saving…" : "Grant access"}
        </button>
      </form>

      <div className="card p-3 shadow-sm">
        <h5 className="mb-3">Current admins</h5>
        {listLoading ? (
          <p>Loading…</p>
        ) : admins.length === 0 ? (
          <p className="text-muted mb-0">No admin accounts registered.</p>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th>UID</th>
                  <th>Email</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {admins.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <code className="small">{row.id}</code>
                      {row.id === user?.uid && (
                        <span className="badge bg-secondary ms-2">You</span>
                      )}
                    </td>
                    <td>{row.admin_email || row.email || "—"}</td>
                    <td className="text-end">
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        disabled={loading || row.id === user?.uid}
                        onClick={() => handleRevoke(row.id)}
                      >
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminsPage;
